/*
  The Tailwind entry has to be the FIRST stylesheet the document parses: cascade
  layer order is set by first appearance, so a layer declared by any stylesheet
  before it would rank wherever that stylesheet put it. See styles/tailwind.css.
*/
import "./styles/tailwind.css";

import { createApp } from "vue";
import { createPinia } from "pinia";
import { createHabitus } from "@livereader/habitus-vue";
import App from "./App.vue";
import { registerPlugins } from "./plugins";
import { configureSocketAuth, connectSocket } from "./socket";
import router from "./router";
import { currentAccessToken, ensureSession, login } from "./shared/composables/useAuth";
import { takePendingInvitation } from "./shared/utils/pendingInvitation";
import { registerWhenSignedIn } from "./shared/utils/serviceWorker";
import { captureEntryUrl } from "./shared/utils/entryUrl";
import "./styles/index.scss";
import "./styles/fonts/roboto.css";
import "flag-icons/css/flag-icons.min.css";

// Before the router runs, which may navigate away from the address the tab was opened with — and
// a client script linked to with `?case=42` still has to read it (`entryQuery`). Memory only.
captureEntryUrl();

const app = createApp(App);

registerPlugins(app);

// `registerComponents: false` keeps the components treeshakeable and, more
// immediately, type-checked: Habitus ships no `GlobalComponents` augmentation,
// so globally registered tags would be untyped in templates. Import what a file
// uses — `import { HabButton } from "@livereader/habitus-vue"`.
app.use(createHabitus({ registerComponents: false }));

app.use(createPinia());

// Mount without waiting: signed-out visitors must see the landing page at once, and
// the router guard's own ensureSession() call shares this in-flight request.
app.mount("#app");

void ensureSession().then(async (signedIn) => {
	// A visitor without a session has asked for the landing page, and a worker is storage on their
	// device: this registers one for a session only. Production builds; the dev server has none to serve.
	void registerWhenSignedIn(signedIn);
	if (!signedIn) return;
	configureSocketAuth({ getToken: currentAccessToken, ensureSession, login });
	connectSocket();
	// An invitation the person set out to accept before creating their account: open it once.
	const invitation = takePendingInvitation();
	if (!invitation) return;
	await router.isReady();
	if (router.currentRoute.value.path !== invitation) await router.replace(invitation);
});
